import React, { useContext, useEffect, useRef } from "react";
import { Carousel } from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import { CarouselRef } from "antd/es/carousel";

import FirstIcon from "assets/images/first-slide-icon.svg";
import ThirthIcon from "assets/images/thirth-slide-icon.svg";
import { ReactComponent as InfoIcon } from "assets/images/icons/info.svg";
import { ReactComponent as CrossIcon } from "assets/images/icons/cross.svg";

import { messages } from "./messages";
import { Currencies } from "@letsbit/malcolmlb";
import { UsdeeuuBridgeFormSliderProps } from "./types";
import InfoScreen from "components/InfoScreen";
import { useWallets } from "hooks/user/useWallets";
import { SWR_WALLET_ADDRESS_KEY } from "hooks/payments/useGetDepositAddress";
import { ApiContext } from "providers/ApiProvider";
import { useMutateAll } from "hooks/utils/useMutateAll";
import { useBridgeAccount } from "hooks/utils/useBridgeAccount";
import { useNavigate } from "react-router-dom";
import { useNotification } from "hooks/utils/useNotification";
import { NotificationStatus } from "components/Notification/types";
import { CircleIconContainer, CurrencyPrice } from "@letsbit/milkylb";
import { Card } from "@letsbit/milkylb";
import { useFormatter } from "hooks/utils/useFormatter";
import { useThemeToBody } from "hooks/utils/useThemeToBody";
import { useGetPaymentsComercialRules } from "hooks/payments/useGetPaymentsComercialRules";
import { errorParser } from "utils/defaultError";
import { useGetDepositAddressPaidStatus } from "hooks/payments/useGetDepositAddressPaidStatus";

export const UsdeeuuBridgeFormSlider: React.FC<
  UsdeeuuBridgeFormSliderProps
> = ({ onDimiss, isOpen }) => {
  const { formatMessage } = useIntl();
  const { formatValue } = useFormatter();
  const { memberInstance } = useContext(ApiContext);
  const { data: wallets } = useWallets();
  const arsWallet = wallets?.["ars"];
  const navigate = useNavigate();
  const {
    usdeeuuWallet,
    bridgePN,
    loading,
    canHaveBridgeAccount,
    awaitingTos,
    depositAddressInformation,
  } = useBridgeAccount();
  const { error: hasntPaid } = useGetDepositAddressPaidStatus(
    usdeeuuWallet?.id,
    bridgePN,
  );

  const { showNotification } = useNotification();
  const { getThemeUrlIcon } = useThemeToBody();

  const { data: commercialRule } = useGetPaymentsComercialRules({
    currency_id: "usdeeuu",
    payment_network_id: "ach_bridge",
  });
  const carouselRef = useRef<CarouselRef | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const { mutateAll } = useMutateAll();

  useEffect(() => {
    const iframe = iframeRef.current;
    if (iframe && !iframe.src && !loading && depositAddressInformation) {
      const iframeURL = new URL(
        canHaveBridgeAccount && awaitingTos
          ? depositAddressInformation.data.tos_link
          : "",
      );
      iframeURL.searchParams.set(
        "redirect_uri",
        `${window.location.origin}/tos_accepted`,
      );
      iframe.src = iframeURL.href;
      iframe.onload = async () => {
        const iframeUrl = iframe.contentWindow?.location.href;

        if (iframeUrl?.includes("signed_agreement_id")) {
          const token = new URL(iframeUrl).searchParams.get(
            "signed_agreement_id",
          );
          try {
            await memberInstance.payments.getMemberDepositAddress({
              currency_id: usdeeuuWallet?.id as Currencies,
              payment_network_id: bridgePN ?? "",
              signed_agreement_id: token ?? undefined,
            });
          } catch (err) {
            switch (errorParser(err)) {
              case "member.deposit_address.commercial_rule_error":
                showNotification({
                  title: formatMessage(messages.notificationErrorTitle),
                  message: commercialRule?.failure_message ?? "",
                  status: NotificationStatus.ERROR,
                });
                break;
              default:
                showNotification({
                  title: formatMessage(messages.notificationErrorTitle),
                  message: formatMessage(
                    messages.notificationGenericErrorSubtitle,
                  ),
                  status: NotificationStatus.ERROR,
                });
            }

            onDimiss();
          }
          mutateAll(SWR_WALLET_ADDRESS_KEY);
          carouselRef.current?.next();
        }
      };
    }
  }, [
    awaitingTos,
    bridgePN,
    canHaveBridgeAccount,
    commercialRule?.failure_message,
    depositAddressInformation,
    formatMessage,
    loading,
    memberInstance.payments,
    mutateAll,
    onDimiss,
    showNotification,
    usdeeuuWallet?.id,
  ]);

  return (
    arsWallet &&
    (isOpen || depositAddressInformation) && (
      <>
        <div className="absolute right-8 top-6 z-50">
          <CrossIcon
            className="h-6 w-6 cursor-pointer fill-[--milkylb-color-secondary-200]"
            onClick={onDimiss}
          />
        </div>
        <Carousel
          ref={carouselRef}
          accessibility={false}
          adaptiveHeight={false}
          arrows={false}
          dots={false}
          infinite={false}
          initialSlide={0}
          slidesToScroll={1}
          slidesToShow={1}
          speed={500}
          swipe={false}
        >
          <div className="h-[calc(90vh-4rem)]" key="first-slide">
            <InfoScreen
              picture={FirstIcon}
              info={
                <div className="flex h-full flex-col items-center text-center font-['Source_Sans_Pro']">
                  <h2 className="my-6 text-4xl font-bold text-[--color-navbar-logo]">
                    <FormattedMessage
                      {...messages.firstTitle}
                      values={{
                        br: <br />,
                      }}
                    />
                  </h2>
                  <div>
                    <h3 className="mb-4 text-sm leading-tight text-[--milkylb-color-neutral-900]">
                      <FormattedMessage
                        {...messages.firstMessage}
                        values={{
                          br: <br />,
                        }}
                      />
                    </h3>
                  </div>
                </div>
              }
              filledButton={{
                text: formatMessage(messages.start),
                onSubmit: carouselRef.current?.next,
              }}
            />
          </div>
          {commercialRule && hasntPaid && (
            <div className="h-[calc(90vh-4rem)] w-full" key="second-slide">
              <InfoScreen
                picture={ThirthIcon}
                info={
                  <div className="flex h-full flex-col items-center text-center font-['Source_Sans_Pro']">
                    <div className="justfy-between flex h-full flex-col">
                      <h2 className="my-6 text-4xl font-bold text-[--color-navbar-logo]">
                        <FormattedMessage {...messages.secondTitle} />
                      </h2>
                      <div>
                        <h3 className="mb-4 text-sm leading-tight text-[--milkylb-color-neutral-900]">
                          {commercialRule.condition_message}
                        </h3>
                      </div>
                      <Card
                        key={arsWallet.id}
                        title={arsWallet.ticker_id.toUpperCase() || ""}
                        subtitle={
                          <div className="flex text-xs font-semibold text-[--milkylb-color-neutral-400]">
                            <p>{arsWallet.name}</p>
                          </div>
                        }
                        icon={
                          <CircleIconContainer
                            icon={getThemeUrlIcon(arsWallet.icon_url)}
                          />
                        }
                        className="mb-1"
                      >
                        <div className="flex gap-4">
                          <CurrencyPrice
                            price={formatValue(
                              arsWallet.balance === ""
                                ? "0"
                                : arsWallet.balance,
                              arsWallet.precision,
                            )}
                            priceCurrency={""}
                            variation={formatMessage(messages.inAccount)}
                          />
                        </div>
                      </Card>
                    </div>
                  </div>
                }
                footer={
                  <div className="mb-2 flex items-center justify-center">
                    <InfoIcon className="mr-2 " />
                    <p className="text-left font-['Source_Sans_Pro'] text-[9px]/[9.9px] font-semibold text-[--milkylb-color-neutral-400]">
                      <FormattedMessage
                        {...messages.secondInfoMessage}
                        values={{ br: <br /> }}
                      />
                    </p>
                  </div>
                }
                filledButton={{
                  text: formatMessage(messages.start),
                  onSubmit: () => {
                    carouselRef.current?.next();
                  },
                }}
              />
            </div>
          )}
          <div className="h-[calc(90vh-4rem)] w-full" key="third-slide">
            <InfoScreen
              noPicture
              info={
                <div className="flex h-full flex-col items-center text-center font-['Source_Sans_Pro']">
                  <h2 className="my-6 text-4xl font-bold text-[--color-navbar-logo]">
                    <FormattedMessage {...messages.thirthTitle} />
                  </h2>
                </div>
              }
              footer={
                <iframe ref={iframeRef} className="w-full" height={380} />
              }
            />
          </div>
          <div className="h-[calc(90vh-4rem)] w-full" key="sixth-slide">
            <InfoScreen
              picture={FirstIcon}
              info={
                <div className="flex h-full flex-col items-center text-center font-['Source_Sans_Pro']">
                  <div className="justfy-between flex h-full flex-col">
                    <h2 className="my-6 text-4xl font-bold text-[--color-navbar-logo]">
                      <FormattedMessage {...messages.fourthTitle} />
                    </h2>
                    <div>
                      <h3 className="mb-4 text-sm leading-tight text-[--milkylb-color-neutral-900]">
                        <FormattedMessage
                          {...messages.fourthMessage}
                          values={{
                            br: <br />,
                          }}
                        />
                      </h3>
                      <h3 className="text-sm leading-tight text-[--milkylb-color-neutral-900]">
                        <b>
                          <FormattedMessage
                            {...messages.fourthMessageBold}
                            values={{
                              br: <br />,
                            }}
                          />
                        </b>
                      </h3>
                    </div>
                  </div>
                </div>
              }
              filledButton={{
                text: formatMessage(messages.goHome),
                onSubmit: () => navigate("/logged/dashboard"),
              }}
            />
          </div>
        </Carousel>
      </>
    )
  );
};
